import React from "react";
// ─── Icons (inline SVG, no external deps) ──────────────────────────────────
export const Icon = ({ name, size = 16 }) => {
  const paths = {
    plus: "M12 5v14M5 12h14",
    search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35",
    x: "M18 6 6 18M6 6l12 12",
    chevronLeft: "M15 18l-6-6 6-6",
    chevronRight: "M9 18l6-6-6-6",
    edit: "M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z",
    users:
      "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    hash: "M4 9h16M4 15h16M10 3 8 21M16 3l-2 18",
    shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z",
    trash:
      "M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16Z",
    archive: "M21 8v13H3V8M1 3h22v5H1zM10 12h4",
    power: "M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10",
    file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6",
    calendar:
      "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z",
    broadcast:
      "M18 16a6 6 0 0 0 0-8M6 8a6 6 0 0 0 0 8M12 20a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3M9 9a3 3 0 0 0 3 3",
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="icon"
    >
      <path d={paths[name]} />
    </svg>
  );
};

// ─── StatusBadge ───────────────────────────────────────────────────────────
export const StatusBadge = ({ status }) => {
  if (!status) return null;

  const isActive = status === "Active" || status === "active";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "999px",
        textTransform: "capitalize",
        background: isActive
          ? "rgba(34, 197, 94, 0.1)"
          : "rgba(239, 68, 68, 0.1)",
        color: isActive ? "#16a34a" : "#ef4444",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
};

// ─── TypeBadge ─────────────────────────────────────────────────────────────
export const TypeBadge = ({ type }) => {
  if (!type) return null;

  const typeClass = type.toLowerCase().replace(/\s/g, "-");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "999px",
        textTransform: "capitalize",
        background:
          type === "Private"
            ? "rgba(0, 98, 254, 0.15)"
            : "rgba(107, 114, 128, 0.12)",
        color: type === "Private" ? "#0050d8" : "#374151",
        whiteSpace: "nowrap",
      }}
    >
      {type}
    </span>
  );
};

// ─── Confirm Modal (shared) - With unique prefixed classes ─────────────────
export function ConfirmModal({
  title,
  message,
  confirmLabel,
  danger,
  onConfirm,
  onClose,
}) {
  return (
    <div className="sc-confirm-overlay" onClick={onClose}>
      <div className="sc-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sc-confirm-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="sc-confirm-body">
          <p>{message}</p>
        </div>
        <div className="sc-confirm-footer">
          <button className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`btn ${danger ? "btn--danger" : "btn--primary"}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Generic Form Drawer (shared) ───────────────────────────────────────────
export function FormDrawer({
  open = true,
  editMode = false,
  initialData = {},
  fields = [],
  onClose,
  onSubmit,
  title = "Form",
  subtitle = "",
  validate,
  submitLabel,
  wide = false,
}) {
  const [form, setForm] = React.useState({});
  const [error, setError] = React.useState("");
  const [closing, setClosing] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    if (!isInitialized || (editMode && initialData)) {
      const init = {};
      fields.forEach((field) => {
        if (field.type === "toggle-group" && field.toggles) {
          field.toggles.forEach((toggle) => {
            init[toggle.name] =
              editMode && initialData[toggle.name] !== undefined
                ? initialData[toggle.name]
                : (toggle.defaultValue ?? false);
          });
        } else if (field.type === "checkbox-group") {
          init[field.name] =
            editMode && initialData[field.name] !== undefined
              ? initialData[field.name]
              : (field.defaultValue ?? []);
        } else {
          init[field.name] =
            editMode && initialData[field.name] !== undefined
              ? initialData[field.name]
              : (field.defaultValue ?? "");
        }
      });
      setForm(init);
      setError("");
      setIsInitialized(true);
    }
  }, [open, editMode]);

  React.useEffect(() => {
    if (!open) {
      setIsInitialized(false);
    }
  }, [open]);

  function handleClose() {
    setClosing(true);
    setTimeout(() => {
      onClose();
      setClosing(false);
    }, 220);
  }

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  }

  function handleSubmit(e) {
    e.preventDefault();

    for (const field of fields) {
      if (field.visible && !field.visible(form)) continue;
      if (field.type === "toggle-group" || field.type === "toggle") continue;

      if (field.required) {
        const value = form[field.name];
        if (value === "" || value === null || value === undefined) {
          setError(`${field.label || field.name} is required.`);
          return;
        }
        if (
          field.type === "checkbox-group" &&
          Array.isArray(value) &&
          value.length === 0
        ) {
          setError(`Please select at least one ${field.label || field.name}.`);
          return;
        }
      }
    }

    if (validate) {
      const customError = validate(form);
      if (customError) {
        setError(customError);
        return;
      }
    }

    onSubmit({ ...form });
    handleClose();
  }

  function renderField(field) {
    if (field.visible && !field.visible(form)) return null;

    const value = form[field.name];
    const disabled = field.disabled || false;

    switch (field.type) {
      case "text":
      case "email":
      case "number":
      case "date":
      case "time":
      case "datetime-local":
        return (
          <input
            id={`fd-${field.name}`}
            type={field.type}
            placeholder={field.placeholder || ""}
            value={value || ""}
            onChange={(e) => updateField(field.name, e.target.value)}
            disabled={disabled}
            className={error && field.required && !value ? "input-error" : ""}
          />
        );

      case "select":
        return (
          <select
            id={`fd-${field.name}`}
            value={value || ""}
            onChange={(e) => updateField(field.name, e.target.value)}
            disabled={disabled}
          >
            {field.placeholder && <option value="">{field.placeholder}</option>}
            {(field.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case "textarea":
        return (
          <textarea
            id={`fd-${field.name}`}
            rows={field.rows || 3}
            placeholder={field.placeholder || ""}
            value={value || ""}
            onChange={(e) => updateField(field.name, e.target.value)}
            disabled={disabled}
          />
        );

      case "toggle":
        return (
          <div className="toggle-row">
            <span className="toggle-row__label">{field.label}</span>
            <button
              type="button"
              className={`toggle-switch ${value ? "toggle-switch--on" : ""}`}
              onClick={() => updateField(field.name, !value)}
              disabled={disabled}
            >
              <span className="toggle-switch__knob" />
            </button>
          </div>
        );

      case "toggle-group":
        return (
          <div className="toggle-block">
            {(field.toggles || []).map((toggle) => (
              <div key={toggle.name} className="toggle-row">
                <span className="toggle-row__label">{toggle.label}</span>
                <button
                  type="button"
                  className={`toggle-switch ${form[toggle.name] ? "toggle-switch--on" : ""}`}
                  onClick={() => updateField(toggle.name, !form[toggle.name])}
                  disabled={toggle.disabled || false}
                >
                  <span className="toggle-switch__knob" />
                </button>
              </div>
            ))}
            {field.note && <p className="drawer__note">{field.note}</p>}
          </div>
        );

      case "checkbox-group":
        return (
          <div className="member-picker">
            {(field.options || []).map((opt) => {
              const isChecked =
                Array.isArray(value) && value.includes(opt.value);
              return (
                <label key={opt.value} className="member-picker__item">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => {
                      const newValue = Array.isArray(value) ? [...value] : [];
                      if (isChecked) {
                        updateField(
                          field.name,
                          newValue.filter((v) => v !== opt.value),
                        );
                      } else {
                        updateField(field.name, [...newValue, opt.value]);
                      }
                    }}
                    disabled={disabled}
                  />
                  <span>{opt.label}</span>
                  {opt.subtitle && (
                    <span className="member-picker__dept">{opt.subtitle}</span>
                  )}
                </label>
              );
            })}
            {field.hint && (
              <span
                className="field-hint"
                style={{ padding: "8px 12px", display: "block" }}
              >
                {Array.isArray(value) ? value.length : 0} selected
              </span>
            )}
          </div>
        );

      case "custom":
        return field.render
          ? field.render({
              value,
              onChange: (v) => updateField(field.name, v),
              form,
              updateField,
            })
          : null;

      default:
        return null;
    }
  }

  if (!open) return null;

  return (
    <div
      className={`drawer-overlay ${closing ? "drawer-overlay--closing" : ""}`}
      onClick={handleClose}
    >
      <div
        className={`drawer ${wide ? "drawer--wide" : ""} ${closing ? "drawer--closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer__header">
          <div>
            <h3>{title}</h3>
            {subtitle && <p className="drawer__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="icon-btn" onClick={handleClose}>
            <Icon name="x" />
          </button>
        </div>

        <form className="drawer__form" onSubmit={handleSubmit}>
          <div className="drawer__body">
            {fields.map((field) => {
              if (field.visible && !field.visible(form)) return null;

              if (field.type === "toggle") {
                return (
                  <div key={field.name} className="form-group">
                    {renderField(field)}
                  </div>
                );
              }

              return (
                <div key={field.name} className="form-group">
                  {field.label && field.type !== "toggle-group" && (
                    <label htmlFor={`fd-${field.name}`}>{field.label}</label>
                  )}
                  {field.type === "toggle-group" && field.label && (
                    <label>{field.label}</label>
                  )}
                  {renderField(field)}
                </div>
              );
            })}
            {error && <div className="form-error">{error}</div>}
          </div>

          <div className="drawer__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              {submitLabel || (editMode ? "Save Changes" : "Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
